import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PricingSection = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Core budgeting tools to get started',
      tagline: 'Ideal for getting started',
      features: [
        { text: 'Core budgeting tools', included: true },
        { text: 'Transaction tracking', included: true },
        { text: 'Manual categorization', included: true },
        { text: 'Basic insights', included: true },
        { text: 'Up to 3 accounts', included: true },
        { text: 'Advanced financial insights', included: false },
        { text: 'Smart alerts & thresholds', included: false },
        { text: 'Payment calendar', included: false },
        { text: 'Priority support', included: false }
      ],
      cta: 'Get Started Free',
      variant: 'outline',
      popular: false
    },
    {
      name: 'Pro',
      price: '$6',
      period: 'per month',
      description: 'Advanced tools for serious budgeters',
      tagline: 'Best value for users serious about managing money',
      features: [
        { text: 'Everything in Free, plus:', included: true, bold: true },
        { text: 'Advanced financial insights', included: true },
        { text: 'Smart alerts and thresholds', included: true },
        { text: 'Payment calendar & reminders', included: true },
        { text: 'Deeper spending analysis', included: true },
        { text: 'Unlimited accounts', included: true },
        { text: 'Unlimited CSV imports', included: true },
        { text: 'Priority support', included: true },
        { text: 'Custom categories', included: true }
      ],
      cta: 'Upgrade to Pro',
      variant: 'default',
      popular: true
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-24 bg-background">
      <div className="mx-auto px-4 md:px-6 lg:px-24 max-w-7xl">
        <div className="text-center mb-10 md:mb-14 lg:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-secondary mb-3 md:mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free and upgrade when you're ready. No hidden fees, no surprises.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
          {plans?.map((plan, index) => (
            <div
              key={index}
              className={`
                relative bg-card rounded-2xl shadow-elevation-3 hover:shadow-elevation-5 
                transition-smooth overflow-hidden
                ${plan?.popular ? 'ring-2 ring-primary lg:scale-105' : ''}
              `}
            >
              {plan?.popular && (
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-2 rounded-bl-xl">
                  <span className="text-sm font-caption font-semibold">Most Popular</span>
                </div>
              )}

              <div className="p-6 md:p-8">
                <div className="mb-6 md:mb-8">
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-card-foreground mb-2">
                    {plan?.name}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-5">
                    {plan?.description}
                  </p>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-4xl md:text-5xl font-heading font-bold text-primary">
                      {plan?.price}
                    </span>
                    <span className="text-base md:text-lg text-muted-foreground">
                      {plan?.period}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground italic">
                    {plan?.tagline}
                  </p>
                </div>

                <Button
                  variant={plan?.variant}
                  size="lg"
                  fullWidth
                  onClick={() => navigate('/register')}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="mb-6 md:mb-8 min-h-[52px] md:min-h-[56px] font-semibold"
                >
                  {plan?.cta}
                </Button>

                <div className="space-y-3 md:space-y-4">
                  <p className="text-sm font-caption font-semibold text-card-foreground mb-4">
                    What's included:
                  </p>
                  {plan?.features?.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="flex items-start gap-3"
                    >
                      <div className={`
                        flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5
                        ${feature?.included ? 'bg-success/20' : 'bg-muted'}
                      `}>
                        <Icon
                          name={feature?.included ? 'Check' : 'X'}
                          size={14}
                          color={feature?.included ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                          strokeWidth={3}
                        />
                      </div>
                      <span className={`
                        text-sm md:text-base
                        ${feature?.included ? 'text-card-foreground' : 'text-muted-foreground line-through'}
                        ${feature?.bold ? 'font-semibold' : ''}
                      `}>
                        {feature?.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10 md:mt-12">
          <p className="text-sm md:text-base text-muted-foreground">
            All plans include bank-level security and 256-bit encryption. Cancel anytime, no questions asked.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;